import React, { useState, useEffect } from 'react';
import { db, handleFirestoreError, OperationType, isQuotaError } from '../firebase';
import { collection, query, getDocs, addDoc, serverTimestamp, orderBy, where, setDoc, doc, onSnapshot } from 'firebase/firestore';
import { getHolidays, getHolidayForDate, Holiday, getHolidayActivitiesForDate } from '../services/holidayService';
import { syncRegularToHoliday } from '../services/attendanceSyncService';
import { refreshDailyStats } from '../services/statsService';
import { getCachedQuery } from '../services/dataCacheService';
import { Truck, Check, Save, Loader2, MessageSquare, AlertCircle, UserCheck, X, ChevronRight, ChevronLeft, Calendar as CalendarIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

const TransportAttendance: React.FC = () => {
  const [transporters, setTransporters] = useState<{ id: string; name: string }[]>([]);
  const [allTransporterRecords, setAllTransporterRecords] = useState<{ id: string; name: string }[]>([]);
  const [attendance, setAttendance] = useState<Record<string, { arrived: boolean; notes: string }>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkReason, setBulkReason] = useState('חג');
  const [customReason, setCustomReason] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [isWeekend, setIsWeekend] = useState(false);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [activities, setActivities] = useState<any[]>([]);

  useEffect(() => {
    fetchTransporters();
    getHolidays().then(setHolidays);
  }, []);

  useEffect(() => {
    if (selectedDate) {
      getHolidayActivitiesForDate(selectedDate).then(setActivities).catch(console.error);
    }
  }, [selectedDate]);

  useEffect(() => {
    const d = new Date(selectedDate);
    const day = d.getDay();
    setIsWeekend(day === 5 || day === 6);
  }, [selectedDate]);

  useEffect(() => {
    if (selectedDate && transporters.length > 0) {
      const attendanceId = `transport_${selectedDate}`;
      const docRef = doc(db, 'transportAttendance', attendanceId);

      const unsubscribe = onSnapshot(docRef, { includeMetadataChanges: true }, (docSnap) => {
        // Skip if we have pending local writes to avoid flickering/reverting UI state
        // before the write is fully acknowledged by the server context
        if (docSnap.metadata.hasPendingWrites && saving) {
          return;
        }

        const initialAttendance: Record<string, { arrived: boolean; notes: string }> = {};
        const holiday = getHolidayForDate(selectedDate, holidays);
        const dynamicTransporters: { id: string; name: string }[] = [...transporters];
        
        // Use pre-fetched activities for this date
        const externalTransportersFromActivities = holiday ? activities
          .map(a => a.transporterName)
          .filter(Boolean)
          .map(name => ({ id: name, name })) : [];
        
        // Add unique external transporters to our list
        externalTransportersFromActivities.forEach(et => {
          if (!dynamicTransporters.find(dt => dt.id === et.id)) {
            dynamicTransporters.push(et);
          }
        });

        // 1. Initialize with defaults based on current transporters
        const d = new Date(selectedDate);
        const day = d.getDay();
        const isWeekendLocal = day === 5 || day === 6;

        dynamicTransporters.forEach(item => {
          if (holiday || isWeekendLocal) {
            initialAttendance[item.id] = { 
              arrived: false, 
              notes: holiday ? `חופשה: ${holiday.name}` : 'שיש/שבת - לא פעיל' 
            };
          } else {
            initialAttendance[item.id] = { arrived: true, notes: '' };
          }
        });

        // 2. Merge with existing data from Firestore if available
        if (docSnap.exists()) {
          const existingData = docSnap.data();
          if (existingData.transporters && Array.isArray(existingData.transporters)) {
            existingData.transporters.forEach((t: any) => {
              // If it's a transporter not in our list, add it to current view
              if (!dynamicTransporters.find(dt => dt.id === t.id)) {
                dynamicTransporters.push({ id: t.id, name: t.name || t.id });
              }

              initialAttendance[t.id] = { 
                arrived: t.arrived ?? (!(holiday || isWeekendLocal)), 
                notes: t.notes ?? (holiday ? `חופשה: ${holiday.name}` : (isWeekendLocal ? 'שישי/שבת - לא פעיל' : ''))
              };
            });
          }
        }
        
        setAllTransporterRecords(dynamicTransporters);
        setAttendance(initialAttendance);
      }, (error) => {
        if (isQuotaError(error)) {
          setMessage({ text: 'מכסת השימוש בבסיס הנתונים הסתיימה להיום.', type: 'error' });
          unsubscribe();
        }
        console.error("Error listening to transport attendance:", error);
      });

      return () => unsubscribe();
    }
  }, [selectedDate, transporters, holidays, activities]);

  const fetchTransporters = async () => {
    setLoading(true);
    try {
      // Fetch transport lines
      const q = query(collection(db, 'transportLines'), orderBy('name'));
      const results = await getCachedQuery('transport_lines', q);
      const transportList = results.map((t: any) => ({
        id: t.id,
        name: t.name
      }));
      
      setTransporters(transportList);
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, 'transportLines');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const autoSaveTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  const triggerAutoSave = (updatedAttendance: Record<string, { arrived: boolean; notes: string }>) => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setAutoSaving(true);
    autoSaveTimerRef.current = setTimeout(() => {
      saveBatchTransport(updatedAttendance);
    }, 1000);
  };

  const toggleArrived = (id: string) => {
    setAttendance(prev => {
      const current = prev[id] || { arrived: true, notes: '' };
      const updated = {
        ...prev,
        [id]: { ...current, arrived: !current.arrived }
      };
      triggerAutoSave(updated);
      return updated;
    });
  };

  const handleNoteChange = (id: string, note: string) => {
    setAttendance(prev => {
      const current = prev[id] || { arrived: true, notes: '' };
      const updated = {
        ...prev,
        [id]: { ...current, notes: note }
      };
      triggerAutoSave(updated);
      return updated;
    });
  };

  const saveBatchTransport = async (currentAttendance: Record<string, { arrived: boolean; notes: string }>) => {
    try {
      const { writeBatch } = await import('firebase/firestore');
      const batch = writeBatch(db);
      
      const attendanceId = `transport_${selectedDate}`;
      const docRef = doc(db, 'transportAttendance', attendanceId);
      
      const transportData = {
        date: selectedDate,
        transporters: allTransporterRecords.map(t => {
          const record = currentAttendance[t.id] || { arrived: true, notes: '' };
          return {
            id: t.id,
            name: t.name,
            arrived: record.arrived,
            notes: record.notes
          };
        }),
        updatedAt: new Date().toISOString(),
        timestamp: serverTimestamp()
      };
      
      batch.set(docRef, transportData, { merge: true });
      
      // Sync to holiday attendance if applicable
      const holidaysList = await getHolidays();
      const holiday = getHolidayForDate(selectedDate, holidaysList);
      
      if (holiday) {
        const syncUpdates = allTransporterRecords.map(t => {
          const record = currentAttendance[t.id] || { arrived: true, notes: '' };
          const isRegular = transporters.find(rt => rt.id === t.id);
          return {
            id: t.id,
            status: (record.arrived ? 'present' : 'absent') as 'present' | 'absent',
            targetType: isRegular ? 'transportLine' : 'transporter' as any
          };
        });
        
        const { syncRegularToHolidayBatchMixed } = await import('../services/attendanceSyncService');
        await syncRegularToHolidayBatchMixed(syncUpdates, selectedDate, holiday.id);
      }
      
      await batch.commit();
      await refreshDailyStats(selectedDate, true);
    } catch (error) {
      console.error("Error auto-saving transport attendance:", error);
    } finally {
      setAutoSaving(false);
    }
  };

  const saveIndividualTransport = async (id: string, arrived: boolean, notes: string) => {
    // Legacy
  };

  const markAllPresent = () => {
    setAttendance(prev => {
      const updated = { ...prev };
      allTransporterRecords.forEach(t => {
        const current = updated[t.id] || { arrived: true, notes: '' };
        updated[t.id] = { ...current, arrived: true };
      });
      triggerAutoSave(updated);
      return updated;
    });
  };

  const handleBulkAction = () => {
    const reason = bulkReason === 'אחר' ? customReason : bulkReason;
    setAttendance(prev => {
      const updated = { ...prev };
      allTransporterRecords.forEach(t => {
        const current = updated[t.id] || { arrived: true, notes: '' };
        updated[t.id] = { ...current, arrived: false, notes: reason };
      });
      triggerAutoSave(updated);
      return updated;
    });
    setShowBulkModal(false);
    setCustomReason('');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { writeBatch } = await import('firebase/firestore');
      const batch = writeBatch(db);
      
      const attendanceId = `transport_${selectedDate}`;
      const docRef = doc(db, 'transportAttendance', attendanceId);
      
      const transportData = {
        date: selectedDate,
        transporters: allTransporterRecords.map(t => {
          const record = attendance[t.id] || { arrived: true, notes: '' };
          return {
            id: t.id,
            name: t.name,
            arrived: record.arrived,
            notes: record.notes
          };
        }),
        updatedAt: new Date().toISOString(),
        timestamp: serverTimestamp()
      };
      
      batch.set(docRef, transportData, { merge: true });
      
      // Sync to holiday attendance if applicable
      const holidaysList = await getHolidays();
      const holiday = getHolidayForDate(selectedDate, holidaysList);
      
      if (holiday) {
        // Use a batch sync for efficiency
        const syncUpdates = allTransporterRecords.map(t => {
          const record = attendance[t.id] || { arrived: true, notes: '' };
          const isRegular = transporters.find(rt => rt.id === t.id);
          return {
            id: t.id,
            status: (record.arrived ? 'present' : 'absent') as 'present' | 'absent',
            targetType: isRegular ? 'transportLine' : 'transporter' as any
          };
        });
        
        const { syncRegularToHolidayBatchMixed } = await import('../services/attendanceSyncService');
        await syncRegularToHolidayBatchMixed(syncUpdates, selectedDate, holiday.id);
      }
      
      await batch.commit();
      
      // Update daily stats summary after bulk update - FORCE write to Firestore so dashboard is accurate
      await refreshDailyStats(selectedDate, true);
      
      setMessage({ text: 'נוכחות מסיעים נשמרה בהצלחה!', type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      if (isQuotaError(error)) {
        setMessage({ text: 'מכסת הכתיבה בבסיס הנתונים הסתיימה להיום.', type: 'error' });
      } else {
        handleFirestoreError(error, OperationType.WRITE, 'transportAttendance');
        setMessage({ text: 'שגיאה בשמירת הנתונים', type: 'error' });
      }
      console.error("Error saving transport attendance:", error);
    } finally {
      setSaving(false);
    }
  };

  const changeDate = (days: number) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-indigo-600 text-white rounded-2xl shadow-lg shadow-indigo-100">
            <Truck size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">נוכחות מסיעים</h2>
            <p className="text-slate-500 text-sm">תיעוד הגעת קווי הסעה ומסיעים</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Date Navigation */}
          <div className="flex items-center bg-white rounded-2xl border border-slate-200 p-1 shadow-sm">
            <button
              onClick={() => changeDate(1)}
              className="p-2 hover:bg-slate-50 rounded-xl text-slate-600 transition-colors"
              title="יום הבא"
            >
              <ChevronRight size={20} />
            </button>
            
            <div className="relative flex items-center px-4 border-x border-slate-100">
              <CalendarIcon size={16} className="text-slate-400 ml-2" />
              <div className="flex flex-col items-start min-w-[100px]">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="bg-transparent border-none text-sm font-bold text-slate-700 focus:ring-0 p-0 cursor-pointer h-5"
                />
                <span className="text-[10px] text-indigo-500 font-bold leading-none">
                  {new Date(selectedDate).toLocaleDateString('he-IL', { weekday: 'long' })}
                </span>
              </div>
            </div>

            <button
              onClick={() => changeDate(-1)}
              className="p-2 hover:bg-slate-50 rounded-xl text-slate-600 transition-colors"
              title="יום קודם"
            >
              <ChevronLeft size={20} />
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {autoSaving ? (
              <span className="flex items-center gap-1.5 text-xs text-indigo-600 bg-indigo-50 border border-indigo-100 px-3 py-1.5 rounded-lg font-bold animate-pulse">
                <Loader2 size={13} className="animate-spin" />
                שומר...
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-1.5 rounded-lg font-bold">
                <Check size={13} />
                נשמר אוטומטית
              </span>
            )}
            <button
              onClick={markAllPresent}
              className="flex items-center gap-2 bg-white text-indigo-600 border border-indigo-200 px-4 py-2 rounded-xl font-bold hover:bg-indigo-50 transition-all shadow-sm"
            >
              <UserCheck size={18} />
              <span>סמן כולם נוכח</span>
            </button>
            <button
              onClick={() => setShowBulkModal(true)}
              className="flex items-center gap-2 bg-white text-amber-600 border border-amber-200 px-4 py-2 rounded-xl font-bold hover:bg-amber-50 transition-all shadow-sm"
            >
              <AlertCircle size={18} />
              <span>דיווח חריגה</span>
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold transition-all shadow-sm ${
                saving 
                  ? 'bg-slate-100 text-slate-400 border border-slate-200' 
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-600'
              }`}
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              <span>שמור עכשיו</span>
            </button>
          </div>
        </div>
      </div>

      {isWeekend && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-center gap-3 text-amber-700">
          <AlertCircle size={20} className="shrink-0" />
          <p className="text-sm font-bold">שים לב: התאריך הנבחר הוא יום שישי או שבת. בית הספר אינו עובד בימים אלו.</p>
        </div>
      )}

      <AnimatePresence>
        {showBulkModal && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <h3 className="text-xl font-bold text-slate-900">דיווח חריגה קבוצתי</h3>
                <button onClick={() => setShowBulkModal(false)} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-slate-600">בחר סיבה לאי-הגעת כל המסיעים היום:</p>
                <div className="grid grid-cols-2 gap-2">
                  {['חג', 'שביתה', 'מזג אוויר', 'אירוע בית ספרי', 'אחר'].map(reason => (
                    <button
                      key={reason}
                      onClick={() => setBulkReason(reason)}
                      className={`px-4 py-3 rounded-xl font-bold border-2 transition-all ${
                        bulkReason === reason
                          ? 'bg-indigo-50 border-indigo-600 text-indigo-700'
                          : 'bg-white border-slate-100 text-slate-600 hover:border-slate-200'
                      }`}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
                {bulkReason === 'אחר' && (
                  <input
                    type="text"
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    placeholder="פרט סיבה..."
                    className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                )}
                <button
                  onClick={handleBulkAction}
                  className="w-full bg-indigo-600 text-white font-bold py-4 rounded-2xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100"
                >
                  עדכן את כולם
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden"
      >
        {allTransporterRecords.length === 0 ? (
          <div className="p-16 text-center">
            <div className="inline-flex p-4 bg-slate-50 text-slate-400 rounded-full mb-4">
              <Truck size={32} />
            </div>
            <p className="text-slate-500 font-medium">לא נמצאו קווי הסעה רשומים במערכת (יש להוסיף קווים בניהול מסיעים)</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-right">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-4 font-bold text-slate-700">שם מסיע / קו</th>
                    <th className="px-6 py-4 font-bold text-slate-700 text-center text-xs">סוג</th>
                    <th className="px-6 py-4 font-bold text-slate-700 text-center">הגיע</th>
                    <th className="px-6 py-4 font-bold text-slate-700">הערות</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {allTransporterRecords.map(t => {
                    const isRegular = transporters.find(rt => rt.id === t.id);
                    return (
                      <tr key={t.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-4 font-medium">
                          <div className="flex flex-col">
                            <span>{t.name}</span>
                            {!isRegular && <span className="text-[10px] text-indigo-500 font-bold">סונכרן מחופשים</span>}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${isRegular ? 'bg-slate-100 text-slate-600' : 'bg-indigo-50 text-indigo-600'}`}>
                            {isRegular ? 'קו רגיל' : 'מסיע חוץ'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex justify-center">
                            <button
                              onClick={() => toggleArrived(t.id)}
                              className={`w-10 h-10 rounded-xl border-2 flex items-center justify-center transition-all ${
                                attendance[t.id]?.arrived
                                  ? 'bg-emerald-600 border-emerald-600 text-white shadow-lg shadow-emerald-100'
                                  : 'border-slate-200 text-transparent hover:border-slate-300'
                              }`}
                            >
                              <Check size={20} strokeWidth={3} />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="relative">
                            <MessageSquare className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                            <input
                              type="text"
                              value={attendance[t.id]?.notes || ''}
                              onChange={(e) => handleNoteChange(t.id, e.target.value)}
                              className="w-full pr-10 pl-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm"
                              placeholder="הערה..."
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card List */}
            <div className="md:hidden divide-y divide-slate-100">
              {allTransporterRecords.map(t => {
                const isRegular = transporters.find(rt => rt.id === t.id);
                return (
                  <div key={t.id} className="p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className="font-bold text-slate-900">{t.name}</span>
                        <span className={`text-[10px] font-bold ${isRegular ? 'text-slate-400' : 'text-indigo-500'}`}>
                          {isRegular ? 'קו רגיל' : 'מסיע חוץ (חופשים)'}
                        </span>
                      </div>
                      <button
                        onClick={() => toggleArrived(t.id)}
                        className={`px-4 py-2 rounded-xl border-2 flex items-center gap-2 transition-all font-bold text-sm ${
                          attendance[t.id]?.arrived
                            ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                            : 'bg-slate-50 border-slate-200 text-slate-400'
                        }`}
                      >
                        {attendance[t.id]?.arrived ? (
                          <>
                            <Check size={16} strokeWidth={3} />
                            <span>הגיע</span>
                          </>
                        ) : (
                          <span>לא הגיע</span>
                        )}
                      </button>
                    </div>
                    
                    <div className="relative">
                      <MessageSquare className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                      <input
                        type="text"
                        value={attendance[t.id]?.notes || ''}
                        onChange={(e) => handleNoteChange(t.id, e.target.value)}
                        className="w-full pr-10 pl-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm"
                        placeholder="הוסף הערה..."
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-6 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {autoSaving && (
                  <div className="flex items-center gap-2 text-indigo-600 text-xs font-medium animate-pulse">
                    <Loader2 size={14} className="animate-spin" />
                    שומר אוטומטית...
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </motion.div>

      {message.text && (
        <div className={`fixed bottom-8 left-1/2 -translate-x-1/2 px-8 py-4 rounded-2xl shadow-2xl text-white font-bold z-50 flex items-center gap-2 ${
          message.type === 'success' ? 'bg-emerald-600' : 'bg-red-600'
        }`}>
          {message.type === 'success' ? <Check size={20} /> : <AlertCircle size={20} />}
          {message.text}
        </div>
      )}
    </div>
  );
};

export default TransportAttendance;
